/**
 * Smoke Queue™ — Shopify theme widget logic
 * -------------------------------------------
 * IMPORTANT — read this before assuming the numbers shown are exact:
 *
 * Shopify's public storefront only exposes a product's TOTAL inventory
 * count across all locations (via the store's public /products/{handle}.js
 * endpoint), not an exact live number for one specific location. What we
 * CAN get per-location, live, without a backend, is a yes/no "available
 * for pickup here" flag (via store_availabilities, rendered at page load).
 *
 * So this widget shows:
 *   - A live-polled TOTAL stock count and bar (refreshes automatically)
 *   - A location-specific "Available for pickup at [X]" / "Not available
 *     at [X] right now" note, based on data available at page load
 *
 * If you want an exact live NUMBER per specific location (not just yes/no),
 * that requires a small backend calling Shopify's Admin API — similar in
 * spirit to (but much simpler than) the Clover integration discussed
 * earlier, since Shopify's own Admin API needs no lengthy app-approval
 * wait. Ask if you'd like that built as a fast-follow.
 */

(function () {
  const section = document.getElementById('smokeQueueSection');
  if (!section) return;

  const THRESH = {
    available: parseInt(section.dataset.sqAvail, 10) || 21,
    limitedMin: parseInt(section.dataset.sqLimMin, 10) || 5,
    limitedMax: parseInt(section.dataset.sqLimMax, 10) || 20,
    almostMax: parseInt(section.dataset.sqAlmostMax, 10) || 4,
  };

  const CAP = 40; // used only for the visual stock bar's 100% reference point
  const POLL_MS = 20000; // how often to re-check live totals
  const MESSAGES = {
    available: ['Smoked low and slow today.', "Today's pitmaster special.", 'Weekend favorite.'],
    limited: ['Going fast today.', 'Order soon - limited portions.'],
    almost: ['Almost gone - order now.', 'Last few portions left.'],
    soldout: ['Fresh out of the smoker.', 'Check back later today.'],
  };
  let messageTick = 0;

  const locationSelect = document.getElementById('sqLocationSelect');
  const STORAGE_KEY = 'tpb_pickup_location';

  function getSelectedLocation() {
    return localStorage.getItem(STORAGE_KEY) || (locationSelect && locationSelect.value) || '';
  }
  function setSelectedLocation(name) {
    localStorage.setItem(STORAGE_KEY, name);
  }
  // restore previously chosen location on load
  if (locationSelect) {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const opt = Array.from(locationSelect.options).find(o => o.value === saved);
      if (opt) locationSelect.value = saved;
    }
    locationSelect.addEventListener('change', () => {
      setSelectedLocation(locationSelect.value);
      renderAllCards();
    });
  }

  function statusFor(quantity) {
    if (quantity <= 0) return 'soldout';
    if (quantity <= THRESH.almostMax) return 'almost';
    if (quantity <= THRESH.limitedMax) return 'limited';
    return 'available';
  }
  function statusLabel(s) {
    return { available: 'Available', limited: 'Limited', almost: 'Almost Gone', soldout: 'Sold Out' }[s];
  }
  function formatCountdown(ms) {
    if (ms <= 0) return '00:00:00';
    const total = Math.floor(ms / 1000);
    const h = String(Math.floor(total / 3600)).padStart(2, '0');
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const s = String(total % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  function readyAtTimestamp(hhmm) {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    const d = new Date();
    d.setHours(h, m, 0, 0);
    if (d.getTime() < Date.now()) d.setDate(d.getDate() + 1);
    return d.getTime();
  }

  const cards = Array.from(document.querySelectorAll('[data-sq-card]'));

  function parseAvailabilities(raw) {
    try {
      // Liquid's `json` filter double-encodes when combined with `escape`
      // in the HTML attribute — decode the HTML entities, then parse JSON.
      const txt = document.createElement('textarea');
      txt.innerHTML = raw;
      return JSON.parse(txt.value) || [];
    } catch (e) {
      return [];
    }
  }

  async function fetchLiveTotal(handle) {
    if (!handle) return null;
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      const variant = data.variants && data.variants[0];
      if (!variant) return null;
      return {
        quantity: typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null,
        available: !!variant.available,
      };
    } catch (e) {
      return null;
    }
  }

  function renderCard(card, liveData) {
    const dot = card.querySelector('[data-sq-dot]');
    const label = card.querySelector('[data-sq-status-label]');
    const bar = card.querySelector('[data-sq-bar]');
    const portionsEl = card.querySelector('[data-sq-portions]');
    const messageEl = card.querySelector('[data-sq-message-el]');
    const countdownWrap = card.querySelector('[data-sq-countdown-wrap]');
    const clockEl = card.querySelector('[data-sq-clock]');
    const notifyWrap = card.querySelector('[data-sq-notify]');

    const quantity = liveData && typeof liveData.quantity === 'number' ? Math.max(0, liveData.quantity) : null;
    const status = quantity === null ? (liveData && liveData.available ? 'available' : 'soldout') : statusFor(quantity);

    card.classList.toggle('sq-is-soldout', status === 'soldout');
    [dot, label, bar].forEach(el => {
      el.className = el.className.replace(/sq-status-\w+/g, '').trim();
      el.classList.add(`sq-status-${status}`);
    });
    label.textContent = statusLabel(status);

    const pct = quantity !== null ? Math.max(0, Math.min(100, Math.round((quantity / CAP) * 100))) : (status === 'soldout' ? 0 : 60);
    bar.style.width = `${status === 'soldout' ? 0 : pct}%`;

    // location-specific pickup note
    const availabilities = parseAvailabilities(card.dataset.sqAvailabilities || '[]');
    const selectedLoc = getSelectedLocation();
    const normalize = s => (s || '').trim().toLowerCase();
    const locMatch = availabilities.find(a => a.location && normalize(a.location.name) === normalize(selectedLoc));
    let portionsText = quantity !== null ? `<strong>${quantity}</strong> total on hand` : (status === 'soldout' ? 'Sold out' : 'In stock');
    if (selectedLoc && locMatch) {
      portionsText += locMatch.available
        ? ` · <span style="color:var(--sq-green)">available for pickup at ${selectedLoc}</span>`
        : ` · <span style="color:var(--sq-red)">not available at ${selectedLoc} right now</span>`;
    }
    portionsEl.innerHTML = portionsText;

    const override = card.dataset.sqMessage;
    const pool = MESSAGES[status] || MESSAGES.available;
    messageEl.textContent = override || pool[messageTick % pool.length];

    // smoking countdown
    const readyAt = readyAtTimestamp(card.dataset.sqReadyAt);
    if (readyAt && readyAt > Date.now()) {
      countdownWrap.style.display = 'flex';
      clockEl.textContent = formatCountdown(readyAt - Date.now());
      card.dataset.sqReadyAtMs = readyAt;
    } else {
      countdownWrap.style.display = 'none';
    }

    // notify-me only for sold out items
    if (notifyWrap) notifyWrap.style.display = status === 'soldout' ? 'block' : 'none';
  }

  async function renderAllCards() {
    for (const card of cards) {
      const handle = card.dataset.sqProductHandle;
      const live = await fetchLiveTotal(handle);
      renderCard(card, live);
    }
  }

  // countdown ticker — updates every second without a full re-render
  setInterval(() => {
    cards.forEach(card => {
      const readyAtMs = Number(card.dataset.sqReadyAtMs);
      if (!readyAtMs) return;
      const clockEl = card.querySelector('[data-sq-clock]');
      const remain = readyAtMs - Date.now();
      if (remain <= 0) {
        card.querySelector('[data-sq-countdown-wrap]').style.display = 'none';
        card.dataset.sqReadyAtMs = '';
        renderAllCards(); // batch re-fetch, likely restocked
      } else if (clockEl) {
        clockEl.textContent = formatCountdown(remain);
      }
    });
  }, 1000);

  // message rotation
  setInterval(() => {
    messageTick++;
    renderAllCards();
  }, 6000);

  // live total polling
  setInterval(renderAllCards, POLL_MS);

  // notify-me interactions (client-side only — wire to Klaviyo/email flow for real sending)
  cards.forEach(card => {
    const openBtn = card.querySelector('[data-sq-notify-open]');
    const form = card.querySelector('[data-sq-notify-form]');
    const submitBtn = card.querySelector('[data-sq-notify-submit]');
    const emailInput = card.querySelector('[data-sq-notify-email]');
    const msgEl = card.querySelector('[data-sq-notify-msg]');
    if (!openBtn) return;
    openBtn.addEventListener('click', () => {
      form.style.display = 'flex';
      openBtn.style.display = 'none';
    });
    submitBtn.addEventListener('click', () => {
      if (!emailInput.value || !emailInput.value.includes('@')) return;
      form.style.display = 'none';
      msgEl.textContent = "You're on the list - we'll email you the moment it's back.";
    });
  });

  renderAllCards();
})();